import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Truck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { SECCIONES_POR_MODULO } from '@/components/layout/secciones';
import { Button } from '@/components/ui/button';
import { Esqueleto, Tabla, Tarjeta, Td, Th, Tr, Vacio } from '@/components/ui/data';
import { Campo, Input, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { avisarError } from '@/lib/avisos';

interface Proveedor {
  id: string;
  nombre: string;
  contacto: string | null;
  telefono: string | null;
  email: string | null;
  rfc: string | null;
  direccion: string | null;
  notas: string | null;
  activo: boolean;
  _count: { productos: number };
}

const CAMPOS = [
  ['nombre', 'Nombre / razón social'],
  ['contacto', 'Persona de contacto'],
  ['telefono', 'Teléfono'],
  ['email', 'Correo'],
  ['rfc', 'RFC'],
  ['direccion', 'Dirección'],
] as const;

function DialogoProveedor({ proveedor, onCerrar }: { proveedor?: Proveedor; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<Record<string, string>>(() =>
    Object.fromEntries([...CAMPOS.map(([k]) => [k, proveedor?.[k] ?? '']), ['notas', proveedor?.notas ?? '']]),
  );
  const guardar = useMutation({
    mutationFn: () => {
      const body = Object.fromEntries(Object.entries(d).map(([k, v]) => [k, v.trim() || (k === 'nombre' ? v : null)]));
      return proveedor ? api(`/proveedores/${proveedor.id}`, { method: 'PATCH', body }) : api('/proveedores', { method: 'POST', body });
    },
    onSuccess: () => {
      toast.success('Proveedor guardado');
      void qc.invalidateQueries({ queryKey: ['proveedores'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });
  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={proveedor ? proveedor.nombre : 'Nuevo proveedor'}
      pie={
        <Button onClick={() => guardar.mutate()} cargando={guardar.isPending} disabled={!d.nombre?.trim()}>
          Guardar
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {CAMPOS.map(([k, etiqueta]) => (
          <Campo key={k} etiqueta={etiqueta} requerido={k === 'nombre'} className={k === 'nombre' || k === 'direccion' ? 'sm:col-span-2' : undefined}>
            {(p) => <Input {...p} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} />}
          </Campo>
        ))}
        <Campo etiqueta="Notas" className="sm:col-span-2">
          {(p) => <Textarea {...p} value={d.notas} onChange={(e) => setD({ ...d, notas: e.target.value })} />}
        </Campo>
      </div>
    </Dialogo>
  );
}

export function ProveedoresPage() {
  const { puede } = useSesion();
  const lista = useQuery({ queryKey: ['proveedores'], queryFn: () => api<Proveedor[]>('/proveedores') });
  const [editar, setEditar] = useState<Proveedor | 'nuevo' | null>(null);
  return (
    <ModuleLayout titulo="Inventario" secciones={SECCIONES_POR_MODULO.inventario}>
      <EncabezadoPagina
        titulo="Proveedores"
        descripcion="Quién surte cada producto."
        acciones={
          puede('inventario.editar') && (
            <Button onClick={() => setEditar('nuevo')}>
              <Plus /> Nuevo proveedor
            </Button>
          )
        }
      />
      <Tarjeta>
        {lista.isLoading ? (
          <Esqueleto className="m-4 h-32" />
        ) : !lista.data?.length ? (
          <Vacio icono={<Truck />} titulo="Sin proveedores" />
        ) : (
          <Tabla>
            <thead>
              <tr>
                <Th>Proveedor</Th>
                <Th>Contacto</Th>
                <Th>Teléfono</Th>
                <Th className="text-right">Productos</Th>
                <Th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {lista.data.map((p) => (
                <Tr key={p.id}>
                  <Td className="font-medium">{p.nombre}</Td>
                  <Td className="text-foreground-muted">{p.contacto ?? '—'}</Td>
                  <Td className="cifras text-foreground-muted">{p.telefono ?? '—'}</Td>
                  <Td className="cifras text-right">{p._count.productos}</Td>
                  <Td>
                    {puede('inventario.editar') && (
                      <Button variante="fantasma" tamano="icono" onClick={() => setEditar(p)} aria-label={`Editar ${p.nombre}`}>
                        <Pencil />
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Tabla>
        )}
      </Tarjeta>
      {editar && <DialogoProveedor proveedor={editar === 'nuevo' ? undefined : editar} onCerrar={() => setEditar(null)} />}
    </ModuleLayout>
  );
}
